import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon,
  Squares2X2Icon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { TotalsRow } from 'components/TableTotals'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useRef, useState } from 'react'
import {
  createAccountingAccountTransaction,
  deleteAccountingAccountTransaction,
  getAccountingAccount,
  getAccountingAccountBeginningBalance,
  getAccountingAccountTransactions,
  importAccountTransactions,
  reconcileAccountingAccountTransactions,
  splitAccountingAccountTransaction,
  updateAccountingAccountTransaction,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Pagination } from 'stories/components'
import { confirm, formatDate, thousandSeperator } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { EditAccountTransactionDialog } from './EditAccountTransactionDialog'
import { ReconcileDialog } from './ReconcileDialog'
import { type IAccountTransaction, AccountTransactionStatusOptions, AccountTransactionTypeOptions } from './types'

export const AccountTransactions = ({ accountNo, onBack }: { accountNo: number; onBack?: () => void }) => {
  const [isLoading, setLoading] = useState(false)
  const [curEditItem, setCurEditItem] = useState<IAccountTransaction | null>()
  const [data, setData] = useState<IAccountTransaction[]>([])
  const [accountName, setAccountName] = useState('')
  const [allAccounts, setAllAccounts] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState(0)
  // Debit/credit over the whole register, summed server-side: the grid holds one page.
  const [totals, setTotals] = useState<{ debit: number; credit: number } | null>(null)
  const [showReconcile, setShowReconcile] = useState(false)
  const [beginningBalance, setBeginningBalance] = useState(0)
  const [filters, setFilters] = useState({
    orderBy: 'date',
    orderDir: '-1',
    pageNum: 0,
    count: 10,
  })
  const pageSizeInput = {
    inputType: 'Select' as const,
    title: 'Items per page',
    options: ['10', '50', '100'],
    value: String(filters.count),
  }
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { hasPermission } = usePermissions()
  const canDelete = hasPermission('Accounting.Account.Delete')

  useEffect(() => {
    // Fetch all accounts
    getAccountingAccount().then((accounts) => {
      setAllAccounts(accounts)
      const account = accounts.find((acc: any) => acc.no === Number(accountNo))
      if (account) {
        setAccountName(`${account.name} (${account.number})`)
      }
    })
  }, [accountNo])

  useEffect(() => {
    fetch()
    fetchBeginningBalance()
  }, [filters])

  const fetchBeginningBalance = async () => {
    const balance = await getAccountingAccountBeginningBalance(Number(accountNo))
    setBeginningBalance(balance || 0)
  }

  const fetch = async () => {
    setLoading(true)
    const result = await getAccountingAccountTransactions(Number(accountNo), filters)
    setData(result.results)
    setTotalCount(result.totalCount)
    setTotals(result.totals || null)
    setLoading(false)
  }

  const onUpdateEditItem = async (item: IAccountTransaction | null) => {
    setCurEditItem(undefined)
    if (!item) return

    setLoading(true)
    try {
      if (item.id < 0) {
        const res = await createAccountingAccountTransaction(item)
        if (!(res as any)?.success) {
          alert((res as any)?.message || 'Failed to create transaction')
        }
      } else {
        const res = await updateAccountingAccountTransaction(item.id, Number(accountNo), item)
        if (!(res as any)?.success) {
          alert((res as any)?.message || 'Failed to update transaction')
        }
      }
      await fetch()
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to save transaction')
    }
    setLoading(false)
  }

  const onDelete = async (item: IAccountTransaction) => {
    const index = data.findIndex((v) => v.id == item.id)
    if (index == -1) return

    if (item.id < 0) {
      const newValues = cloneDeep(data)
      newValues.splice(index, 1)
      setData(newValues)
      return
    }

    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure want to delete this transaction?
        <br />
        <span className="text-gray-600">Ref No: {item.refNo}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    try {
      await deleteAccountingAccountTransaction(item.id, Number(accountNo))

      const newValues = cloneDeep(data)
      newValues.splice(index, 1)
      setData(newValues)
      setTotalCount(totalCount - 1)
    } catch (e) {}
    setLoading(false)
  }

  const onPageNavigate = (num: number) => {
    const newFilters = cloneDeep(filters)
    newFilters.pageNum = num
    setFilters(newFilters)
  }

  const onPageSizeChange = (value: string) => {
    const newFilters = cloneDeep(filters)
    newFilters.count = Number(value)
    newFilters.pageNum = 0 // Reset to first page when changing page size
    setFilters(newFilters)
  }

  const onSplit = async (item: IAccountTransaction) => {
    const lines = item.distributionLines?.filter((l) => l.account || Number(l.debit) || Number(l.credit)) || []
    if (lines.length < 2) {
      alert(
        'Save at least 2 distribution lines first. Edit the transaction, fill the Distribution table with 2+ lines, and Submit. Then click Split.',
      )
      return
    }
    const confirmContent = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure you want to split this transaction? One transaction will become {lines.length} separate
        transactions.
        <br />
        <span className="text-gray-600">Ref No: {item.refNo}</span>
      </div>
    )
    const result = await confirm(confirmContent)
    if (!result) return
    const payload = {
      lines: lines.map((l) => ({
        account: Number(l.account),
        payment: Number(l.debit) || 0,
        deposit: Number(l.credit) || 0,
        memo: l.description || undefined,
        class: l.class || undefined,
      })),
    }
    setLoading(true)
    try {
      const res = await splitAccountingAccountTransaction(Number(accountNo), item.id, payload)
      if ((res as any)?.success) {
        await fetch()
      } else {
        alert((res as any)?.message || 'Split failed')
      }
    } catch (e: any) {
      alert(e?.message || e?.response?.data?.message || 'Split failed')
    }
    setLoading(false)
  }

  const onReconcile = () => {
    setShowReconcile(true)
  }

  const onReconcileClose = async (reconciliationData?: {
    transactionIds: number[]
    statementDate: string
    endingBalance: number
    serviceCharge: number
    interest: number
  }) => {
    setShowReconcile(false)
    if (reconciliationData && reconciliationData.transactionIds.length > 0) {
      setLoading(true)
      try {
        await reconcileAccountingAccountTransactions(Number(accountNo), reconciliationData)
        await fetch()
        await fetchBeginningBalance()
      } catch (e) {
        console.error('Reconciliation error:', e)
      }
      setLoading(false)
    }
  }

  const onImportClick = () => {
    fileInputRef.current?.click()
  }

  const onImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const result = await confirm(
      'Are you sure you want to import transactions from this file? This will add all transactions to this account.',
    )
    if (!result) {
      event.target.value = ''
      return
    }

    setLoading(true)
    try {
      const response = await importAccountTransactions(Number(accountNo), file)
      if (response.success) {
        alert(
          `Successfully imported ${response.imported} transactions${
            response.errors?.length ? `\n\nWarnings:\n${response.errors.join('\n')}` : ''
          }`,
        )
        await fetch()
        await fetchBeginningBalance()
      } else {
        alert(`Import failed:\n${response.errors?.join('\n') || 'Unknown error'}`)
      }
    } catch (e: any) {
      console.error('Import error:', e)
      alert(`Import error: ${e.message || 'Unknown error'}`)
    }
    setLoading(false)
    event.target.value = ''
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center pb-5">
          {onBack && (
            <span
              className="mr-3 cursor-pointer text-shade-blue hover:text-blue-700"
              onClick={onBack}
              title="Back to Accounts"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </span>
          )}
          Account Transactions - {accountName}
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h1>
        <div className="flex gap-2">
          <div
            className="flex items-center gap-2 hover:cursor-pointer hover:underline text-shade-blue"
            onClick={onImportClick}
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Import
          </div>
          <Button onClick={onReconcile} color="green" className="">
            Reconcile
          </Button>
          <Button onClick={() => setCurEditItem(null)} className="">
            Add
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={onImportFile}
        style={{ display: 'none' }}
      />

      <div className="relative overflow-auto">
        <LayoutLoading show={isLoading} />

        <div className="sm:rounded-lg mb-8">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-3 py-3 w-[20px]">
                  No
                </th>
                <th scope="col" className="px-3 py-3">
                  Date
                </th>
                <th scope="col" className="px-3 py-3">
                  Ref No
                </th>
                <th scope="col" className="px-3 py-3">
                  Type
                </th>
                <th scope="col" className="px-3 py-3">
                  Payee
                </th>
                <th scope="col" className="px-3 py-3">
                  Account
                </th>
                <th scope="col" className="px-3 py-3">
                  Memo
                </th>
                <th scope="col" className="px-3 py-3">
                  Class
                </th>
                <th scope="col" className="px-3 py-3">
                  Location
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Debit
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Credit
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Balance
                </th>
                <th scope="col" className="px-3 py-3">
                  Status
                </th>
                <th scope="col" className="px-3 py-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                return (
                  <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700" key={index}>
                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {index + 1 + filters.count * filters.pageNum}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {formatDate(item.date)}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {item.refNo}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {AccountTransactionTypeOptions[item.type]}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {item.payee}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {item.distributionLines && item.distributionLines.length > 0
                        ? '-Split-'
                        : item.account
                        ? allAccounts.find((acc) => acc.no === item.account)?.name || item.account
                        : ''}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {item.memo}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {item.class}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {item.location}
                    </td>

                    <td
                      scope="row"
                      className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap text-right"
                    >
                      {Number(item.payment) ? thousandSeperator(item.payment) : ''}
                    </td>

                    <td
                      scope="row"
                      className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap text-right"
                    >
                      {Number(item.deposit) ? thousandSeperator(item.deposit) : ''}
                    </td>

                    <td
                      scope="row"
                      className="px-3 py-1 text-gray-900 dark:text-white whitespace-nowrap text-right font-semibold"
                    >
                      {item.balance !== undefined ? `$${thousandSeperator(item.balance)}` : ''}
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {AccountTransactionStatusOptions[item.status]}
                    </td>

                    <td className="px-6 py-1">
                      <div className="flex gap-2 items-center">
                        <span
                          className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                          onClick={() => setCurEditItem(item)}
                          title="Edit"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                        {(() => {
                          const validDist =
                            item.distributionLines?.filter((l) => l.account || Number(l.debit) || Number(l.credit)) ||
                            []
                          const canSplit = validDist.length >= 2
                          return (
                            <span
                              className={
                                canSplit
                                  ? 'text-shade-blue p-1 hover-shadow1 cursor-pointer'
                                  : 'text-gray-400 p-1 cursor-not-allowed opacity-50'
                              }
                              onClick={() => canSplit && onSplit(item)}
                              title={'Split'}
                            >
                              <Squares2X2Icon className="w-4 h-4" />
                            </span>
                          )
                        })()}
                        {canDelete && (
                          <span
                            className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                            onClick={() => onDelete(item)}
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4"></TrashIcon>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {data.length > 0 && totals && (
              <TotalsRow
                tdClass="px-3 py-2"
                cells={[
                  'Totals',
                  '',
                  '',
                  '',
                  '',
                  '',
                  '',
                  '',
                  '',
                  totals.debit,
                  totals.credit,
                  '',
                  '',
                  '',
                ]}
              />
            )}
          </table>
        </div>

        <div className="flex justify-end items-center mt-3 mb-3 gap-4">
          <div className="w-40">
            <RenderInput
              input={pageSizeInput}
              Key="pageSize"
              onChange={(_key: string, value: string) => onPageSizeChange(value)}
            />
          </div>
          <Pagination
            totalCount={totalCount}
            itemCountPerPage={filters.count}
            onNavigate={onPageNavigate}
            pageNum={filters.pageNum}
          />
        </div>

        {curEditItem !== undefined && (
          <EditAccountTransactionDialog
            item={curEditItem}
            onClose={onUpdateEditItem}
            accountNo={Number(accountNo)}
            allAccounts={allAccounts}
            openedFromLedger={false}
          />
        )}

        {showReconcile && (
          <ReconcileDialog
            isOpen={showReconcile}
            onClose={onReconcileClose}
            transactions={data}
            accountName={accountName}
            beginningBalance={beginningBalance}
          />
        )}
      </div>
    </div>
  )
}
